import { HttpClient } from '@angular/common/http';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { setLicenseKey } from '@mescius/wijmo';
import { environment } from '@src/environments/environment';
import FroalaEditor from 'froala-editor';
import { catchError, exhaustMap, forkJoin, from, map, Observable, of } from 'rxjs';

import { CUSTOM_EDITOR_BUTTONS, CUSTOM_EDITOR_BUTTONS_ACTIONS, CUSTOM_MAT_ICONS_DEFINITIONS, PRODUCTION_COMMAND_BUTTONS } from './common/enums/constants';
import { NomWebServicesJson } from './common/services/nom-web-services-json';
import { ML2HtmlUtils } from './common/utils/ml-2-html-utils';
import { getUrlPrefix } from './common/utils/util';

export function initializeAppFactory(httpClient: HttpClient, nomWebServicesJson: NomWebServicesJson, matIconRegistry: MatIconRegistry, domSanitizer: DomSanitizer): () => Observable<any> {
    PRODUCTION_COMMAND_BUTTONS.forEach(command => {
        const iconName = ML2HtmlUtils.getCommandTypeIcon(command);
        const title = command.charAt(0).toUpperCase() + command.slice(1).toLowerCase();
        defineAndRegisterCommand(command, title, iconName);
    });

    CUSTOM_EDITOR_BUTTONS.forEach(button => {
        const iconName = button;
        const title = button.charAt(0).toUpperCase() + button.slice(1).toLowerCase();
        defineAndRegisterCustomEditorButton(button, title, iconName, CUSTOM_EDITOR_BUTTONS_ACTIONS[button]);
    });

    return () =>
        httpClient.get<Config>('./assets/config.json').pipe(
            map(data => {
                if (environment.production) {
                    environment.user_id_space_replacement_character = data.user_id_space_replacement_character;
                    environment.show_warnings = data.show_warnings;
                    environment.show_errors = data.show_errors;
                    environment.pst_version = data.pst_version;
                    environment.pst_serial_number = data.pst_serial_number;
                    environment.translated_label_login_error = data.translated_label_login_error;
                    environment.translated_label_license_expired = data.translated_label_license_expired;
                    environment.translated_label_ssogoogle = data.translated_label_ssogoogle;
                    environment.translated_label_ssomicrosoft = data.translated_label_ssomicrosoft;
                    environment.translated_label_ssomicrosoftlive = data.translated_label_ssomicrosoftlive;
                    environment.service_address = data.service_address;
                    environment.backup_service_address = data.backup_service_address;
                    environment.discover_api_base_url = data.discover_api_base_url;
                    environment.solr_poll_interval_seconds = data.solr_poll_interval_seconds;
                    environment.default_domain = data.default_domain;
                    environment.virtual_directory = data.virtual_directory;
                    environment.merlin_base_url = data.merlin_base_url;
                    environment.merlin_media_base_url = data.merlin_media_base_url;
                    environment.open_ai_api_key = data.open_ai_api_key;
                }
                const urlPrefix = getUrlPrefix();
                CUSTOM_MAT_ICONS_DEFINITIONS.forEach(icon => {
                    matIconRegistry.addSvgIcon(icon.name, domSanitizer.bypassSecurityTrustResourceUrl(`${urlPrefix}/${icon.path}`));
                });
                nomWebServicesJson.initFromEnvironment();
            }),
            exhaustMap(() => {
                return from(nomWebServicesJson.ping());
            }),
            exhaustMap(() => {
                return forkJoin([
                    from(nomWebServicesJson.get<SSOConfigSettings>('/api/SSOConfigSetings', { observe: 'response' })).pipe(map(r => r.body)),
                    from(nomWebServicesJson.get<SSOSettings[]>(`/api/SSOSettings/${navigator.language}`, { observe: 'response' })).pipe(map(r => r.body)),
                ]);
            }),
            map(([ssoConfigSettings, ssoSettings]) => {
                window.name = 'APStoryTelling';
                nomWebServicesJson.ssoConfigSettings = ssoConfigSettings;
                nomWebServicesJson.ssoProviders = ssoSettings || [];
                viewPortAdjustment();
                checkForSSO();
                setLicenseKey(
                    'US Viking LLC,937829645358722#B0IONQkIsISP3cndsVnMpRDdNlmUrY6Vwo5cGNGR8IGSqdEeah5UwJUNypmdD3WdUVDTzZnW5g6dHF4cLhFZvsiTrZGd9lXbGpkU52Ua654NE9UZDlDbJFWbrlUMQp7d4YnMxYkMxR6LjRERtl5Yzd5Y5J5awg4NalzUyV4d0d4VrplUFJ5KoxEatdmS8gjW4JVVXRFUrlVRhVmZ8FDbQlHMp5WVEVjR8NmZRhGNqJ5UUVEbKNEe9tScpl6LOJ5RENjSS3kMrJVQBhjWzcjTp9kVqlGWwN4Y4MlSBdjZxgnVyRWWGpXR9JlbRFEa8dkZtZkcvFXN9VzczFGTTlVc6E7bhFTT9NnMsZ5b7UVRvlDbERXNzk5LKBlWjVVMjhUMnV7Nlpmch3EN4VWZwVHRtZjTzwWdoNHarUWUlJmR9U7MxNjViVnba3WTyhHer9GUCJ5L7VnNDhlRt94MZ96YPJiOiMlIsICO6IUODdjQiojIIJCLzkzNzETNykTM0IicfJye35XX3JSSwIjUiojIDJCLi86bpNnblRHeFBCI4VWZoNFelxmRg2Wbql6ViojIOJyes4nI5kkTRJiOiMkIsIibvl6cuVGd8VEIgIXZ7VWaWRncvBXZSBybtpWaXJiOi8kI1xSfis4N8gkI0IyQiwiIu3Waz9WZ4hXRgAydvJVa4xWdNBybtpWaXJiOi8kI1xSfiQjR6QkI0IyQiwiIu3Waz9WZ4hXRgACUBx4TgAybtpWaXJiOi8kI1xSfiMzQwIkI0IyQiwiIlJ7bDBybtpWaXJiOi8kI1xSfiUFO7EkI0IyQiwiIu3Waz9WZ4hXRgACdyFGaDxWYpNmbh9WaGBybtpWaXJiOi8kI1tlOiQmcQJCLiATMwEzNwAiMwUDM4IDMyIiOiQncDJCLiMETMByZul6apZFITVlI0ISYONkIsIiMycDO5MTN4YTOygzNzkjI0ICZJJCL355W0IyZsZmIsISM6RjMwIjI0IiclZnIsU6csFmZ0IiczRmI1pjIs9IQws'
                );
            }),
            catchError((err: unknown) => {
                console.log(err);
                return of();
            })
        );
}

const viewPortAdjustment = () => {
    const viewport = document.getElementById('viewport');
    if (navigator.userAgent.match(/iPhone/i)) {
        viewport.setAttribute('content', 'viewport-fit=auto, width=device-width, height=device-height, initial-scale=1, maximum-scale=1, user-scalable=1, minimal-ui');
    } else {
        viewport.setAttribute('content', 'viewport-fit=cover, width=device-width, height=device-height, initial-scale=1, maximum-scale=1, user-scalable=1, minimal-ui');
    }
};

const checkForSSO = () => {
    if (window.location.href !== null) {
        // if the URL is not null
        const matchSSOToken = /idToken=(.*)/;
        const foundSSOToken = window.location.href.match(matchSSOToken);
        if (foundSSOToken) {
            localStorage.setItem('idToken', foundSSOToken[1]);
            let newUrl = window.location.origin;
            if (newUrl.endsWith('/')) newUrl = newUrl.substring(0, newUrl.lastIndexOf('/'));
            window.location.href = environment.virtual_directory ? `${newUrl}/${environment.virtual_directory}/#/login` : `${newUrl}/#/login`;
        }
    }
};

const insertIcon = (icon: string) => {
    return `<span class="material-symbols-outlined">${icon}</span>`;
};
const defineAndRegisterCommand = (commandName, title, materialIcon): string => {
    const templateName = `material_design_${commandName}`;
    FroalaEditor.DefineIconTemplate(templateName, insertIcon(materialIcon));
    FroalaEditor.DefineIcon(materialIcon, { NAME: commandName, template: templateName });
    FroalaEditor.RegisterCommand(commandName, {
        title: title,
        icon: materialIcon,
        focus: true,
        undo: true,
        refreshAfterCallback: true,
        callback: function () {
            this.events.trigger('productionCommand', [title]);
        },
    });
    return commandName;
};

const defineAndRegisterCustomEditorButton = (commandName, title, materialIcon, action): string => {
    const templateName = `custom_buttons_${commandName}`;
    FroalaEditor.DefineIconTemplate(templateName, insertIcon(materialIcon));
    FroalaEditor.DefineIcon(materialIcon, { NAME: commandName, template: templateName });
    FroalaEditor.RegisterCommand(commandName, {
        title: title,
        icon: materialIcon,
        focus: true,
        undo: true,
        refreshAfterCallback: true,
        callback: function () {
            this.events.trigger(action, [title]);
        },
    });
    return commandName;
};
